/*
 * Touch-Me — sticky checkout knop
 * ------------------------------------------------------------------
 * Toont een zwevende "Afrekenen"-knop rechtsonder zodra er producten in de
 * winkelwagen zitten, en verbergt hem weer zodra de winkelwagen leeg is.
 *
 * - Bij het laden van de pagina (ook na een refresh) staat de beginstatus al
 *   server-side goed, gerenderd door snippets/tm-sticky-checkout.liquid op
 *   basis van cart.item_count. Dit script houdt de knop daarna live
 *   gesynchroniseerd via het thema's eigen "cart:updated" event, hetzelfde
 *   event dat de cart-drawer, de header-teller en de cart-reminder gebruiken
 *   (zie assets/main.build.js en assets/component-cart-reminder.build.js).
 *   De listener staat in een try/catch, zodat een fout hier nooit de andere
 *   listeners op hetzelfde event (zoals de cart-drawer) kan verstoren.
 * - Op de productpagina staat al een sticky "voeg toe aan winkelwagen"-balk
 *   onderin (.product-sticky-add-to-cart, ook aanwezig in de quick-view van
 *   een productkaart), en de cart-reminder-popup (.cart-reminder) kan ook
 *   rechtsonder verschijnen. Beide zijn "position: fixed" met een eigen
 *   afstand tot de onderkant van het scherm (bv. 32px op desktop voor de
 *   sticky-balk, of een safe-area-offset op mobiel). Dit script meet de
 *   werkelijke, gerenderde positie van beide elementen (niet alleen hun
 *   hoogte) en schuift de checkout-knop daar altijd boven, zodat hij nooit
 *   overlapt, ook niet wanneer die afstand tot de onderkant verandert.
 *
 *   Let op: "position: fixed"-elementen hebben altijd offsetParent === null,
 *   ook wanneer ze gewoon zichtbaar zijn. Zichtbaarheid wordt daarom bepaald
 *   via getComputedStyle (display/visibility/opacity), niet via offsetParent.
 * - Tijdens het scrollen (bijvoorbeeld door een collectiepagina met
 *   productkaarten) dimt de knop en staat hij op "pointer-events: none",
 *   zodat hij nooit een klik op een onderliggende "Toevoegen aan
 *   winkelwagen"-knop van een productkaart kan onderscheppen. Zodra het
 *   scrollen stopt, wordt hij weer volledig zichtbaar en klikbaar.
 */
(function () {
	"use strict";

	var SELECTOR = "[data-tm-sticky-checkout]";
	var VISIBLE_CLASS = "tm-sticky-checkout--visible";
	var IDLE_CLASS = "tm-sticky-checkout--idle";
	var STACK_GAP = 16; // ruimte (px) tussen de knop en het element eronder
	var STACK_SELECTORS = [".product-sticky-add-to-cart", ".cart-reminder"];
	var SCROLL_IDLE_DELAY = 350; // ms na het stoppen met scrollen voordat de knop weer klikbaar wordt

	var button = null;
	var pendingFrame = null;
	var scrollIdleTimer = null;

	function safely(fn) {
		return function () {
			try {
				return fn.apply(this, arguments);
			} catch (error) {
				// Nooit laten breken: een fout hier mag nooit andere listeners
				// op hetzelfde event (bv. de cart-drawer) blokkeren.
				if (window.console && window.console.warn) {
					window.console.warn("[tm-sticky-checkout]", error);
				}
			}
		};
	}

	function isElementVisible(el) {
		if (!el) {
			return false;
		}

		// offsetParent is niet bruikbaar hier: voor "position: fixed"-elementen
		// (zoals de sticky add-to-cart-balk en de cart-reminder) is offsetParent
		// altijd null, ook als het element gewoon zichtbaar op het scherm staat.
		var style = window.getComputedStyle(el);
		if (style.display === "none" || style.visibility === "hidden" || parseFloat(style.opacity) === 0) {
			return false;
		}

		var rect = el.getBoundingClientRect();
		var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

		// Extra controle: sommige elementen blijven in de DOM aanwezig met
		// display:flex, maar staan (nog) volledig buiten beeld geschoven
		// (bv. bottom: -100% voordat de "is-active" class actief is).
		return rect.width > 0 && rect.height > 0 && rect.top < viewportHeight && rect.bottom > 0;
	}

	function updateStackOffset() {
		if (!button) {
			return;
		}

		var offset = 0;
		var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

		STACK_SELECTORS.forEach(function (selector) {
			var el = document.querySelector(selector);
			if (!isElementVisible(el)) {
				return;
			}

			// Afstand vanaf de onderkant van het scherm tot de bovenkant van dit
			// element. Dit is bewust gebaseerd op de werkelijke, gerenderde
			// positie (rect.top) en niet alleen op de hoogte van het element,
			// want zo'n element kan zelf ook al los van de onderrand van het
			// scherm staan (bv. bottom: 32px op desktop voor de sticky
			// "voeg toe aan winkelwagen"-balk). Bij meerdere zichtbare
			// elementen tegelijk houden we rekening met de hoogste, ze staan
			// namelijk niet op elkaar gestapeld maar elk apart t.o.v. de
			// onderkant van het scherm.
			var rect = el.getBoundingClientRect();
			var spaceFromBottom = viewportHeight - rect.top;
			if (spaceFromBottom > offset) {
				offset = spaceFromBottom;
			}
		});

		if (offset > 0) {
			offset += STACK_GAP;
		}

		document.documentElement.style.setProperty("--tm-sticky-checkout-offset", offset + "px");
	}

	function scheduleStackUpdate() {
		if (pendingFrame) {
			return;
		}
		pendingFrame = window.requestAnimationFrame(function () {
			pendingFrame = null;
			updateStackOffset();
		});
	}

	function setVisible(isVisible) {
		if (!button) {
			return;
		}
		button.classList.toggle(VISIBLE_CLASS, isVisible);
		button.setAttribute("aria-hidden", isVisible ? "false" : "true");
		button.tabIndex = isVisible ? 0 : -1;
	}

	function handleScroll() {
		if (!button) {
			return;
		}
		button.classList.remove(IDLE_CLASS);
		if (scrollIdleTimer) {
			window.clearTimeout(scrollIdleTimer);
		}
		scrollIdleTimer = window.setTimeout(function () {
			button.classList.add(IDLE_CLASS);
		}, SCROLL_IDLE_DELAY);
	}

	var onCartUpdated = safely(function (event) {
		var itemCount =
			event && typeof event.item_count === "number"
				? event.item_count
				: (window.themeCore.cartObject && window.themeCore.cartObject.item_count) || 0;

		window.themeCore.cartObject = window.themeCore.cartObject || {};
		window.themeCore.cartObject.item_count = itemCount;

		setVisible(itemCount > 0);
		updateStackOffset();
	});

	function handleStackTransitionEnd(event) {
		var target = event.target;
		if (!target || typeof target.matches !== "function") {
			return;
		}
		var matchesStackElement = STACK_SELECTORS.some(function (selector) {
			return target.matches(selector);
		});
		if (matchesStackElement) {
			scheduleStackUpdate();
		}
	}

	function observeStackChanges() {
		if (window.ResizeObserver) {
			var resizeObserver = new ResizeObserver(safely(scheduleStackUpdate));
			STACK_SELECTORS.forEach(function (selector) {
				var el = document.querySelector(selector);
				if (el) {
					resizeObserver.observe(el);
				}
			});
		}

		// Dekt zowel de cart-reminder-popup (direct in <body> ingevoegd) als de
		// sticky "voeg toe"-balk in een quick-view die dieper in de pagina wordt
		// geladen, plus eventuele class-toggles (bv. .is-active) daarbinnen.
		new MutationObserver(safely(scheduleStackUpdate)).observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["class", "style"]
		});

		// Vangt het moment direct na afloop van de in/uit-animatie van de
		// sticky-balk of de cart-reminder, zodat de eindpositie altijd exact
		// klopt, ook los van de MutationObserver/RAF-timing tijdens de animatie.
		document.body.addEventListener("transitionend", safely(handleStackTransitionEnd), true);
		document.body.addEventListener("animationend", safely(handleStackTransitionEnd), true);

		window.addEventListener("resize", safely(scheduleStackUpdate));
		window.addEventListener("orientationchange", safely(scheduleStackUpdate));
		window.addEventListener("scroll", safely(handleScroll), { passive: true });
	}

	function init() {
		button = document.querySelector(SELECTOR);
		if (!button) {
			return;
		}

		button.classList.add(IDLE_CLASS);

		var initialItemCount = (window.themeCore.cartObject && window.themeCore.cartObject.item_count) || 0;
		setVisible(initialItemCount > 0);
		updateStackOffset();

		window.themeCore.EventBus.listen("cart:updated", onCartUpdated);
		observeStackChanges();
	}

	function action() {
		if (!window.themeCore || window.themeCore.stickyCheckoutInitiated) {
			return;
		}
		window.themeCore.stickyCheckoutInitiated = true;
		safely(init)();
	}

	if (window.themeCore && window.themeCore.loaded) {
		action();
	} else {
		document.addEventListener("theme:all:loaded", action, { once: true });
	}
})();